import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loading } from "@/components/common/Loading";
import { OaReasonDialog } from "./OaReasonDialog";

// Otter Assist admin — operational controls (OAD-121), provider configuration (OAD-122) and the
// read-only matching configuration (OAD-123, V2.1 frozen).
//
// A runtime override wins; clearing it restores the deployed value EXACTLY. Secrets are write-only:
// the console can save one and see a mask, and can never read one back.

export function OaControls() {
  const qc = useQueryClient();
  const [pending, setPending] = useState(null);   // { key, value, clear }
  const [values, setValues] = useState({});
  const [secrets, setSecrets] = useState({});
  const [providerOpen, setProviderOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["oa-admin-controls"],
    queryFn: async () => (await api.get("/admin/otter-assist/controls")).data,
  });

  const test = useMutation({
    mutationFn: async () => (await api.post("/admin/otter-assist/provider/test")).data,
    onSuccess: (r) => {
      const text = `Chat ${r.chat?.ok ? "ok" : "not configured"} · Embeddings ${
        r.embed?.ok ? "ok" : "not configured"}`;
      setTestResult({ ok: !!(r.chat?.ok && r.embed?.ok), text });
      toast[r.chat?.ok && r.embed?.ok ? "success" : "error"](text);
    },
    onError: () => {
      setTestResult({ ok: false, text: "The connection test could not run." });
      toast.error("The connection test could not run.");
    },
  });

  if (isLoading) return <Loading />;
  if (!data) return null;
  const editable = data.editable;
  const provider = data.provider;

  return (
    <div className="space-y-4" data-testid="oa-admin-controls">
      <Card className="p-4" data-testid="oa-controls-flags">
        <h3 className="text-sm font-[640]">Operational controls</h3>
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          A runtime override wins over the deployed value. Clearing an override restores the deployed
          value exactly.
        </p>
        <ul className="mt-3 divide-y divide-border">
          {(data.flags || []).map((f) => (
            <li key={f.key} className="flex flex-wrap items-start gap-3 py-2.5"
              data-testid={`oa-control-${f.key}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-[600]">{f.key.replace(/_/g, " ")}</span>
                  <Badge variant={f.effective ? "default" : "secondary"}
                    data-testid={`oa-control-state-${f.key}`}>
                    {f.effective ? "on" : "off"}
                  </Badge>
                  {f.source === "runtime" && (
                    <Badge variant="outline" data-testid={`oa-control-source-${f.key}`}>
                      runtime override
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{f.effect}</p>
                {f.backlog_risk && (
                  <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-800"
                    data-testid={`oa-control-warning-${f.key}`}>
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    {f.effective === false
                      ? "Turning this on can process a historic backlog and resume outreach immediately."
                      : "This control is on. Turning it off and on again can process a historic backlog."}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {f.source === "runtime" && editable && (
                  <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                    onClick={() => setPending({ key: f.key, value: null, clear: true })}
                    data-testid={`oa-control-clear-${f.key}`}>Clear override</Button>
                )}
                <Switch checked={!!f.effective} disabled={!editable}
                  onCheckedChange={(v) => setPending({ key: f.key, value: v, clear: false })}
                  data-testid={`oa-control-toggle-${f.key}`} />
              </div>
            </li>
          ))}
        </ul>
        {!editable && (
          <p className="mt-3 text-[11px] italic text-muted-foreground"
            data-testid="oa-controls-readonly">
            You can view these controls but not change them.
          </p>
        )}
      </Card>

      {provider ? (
        <Card className="p-4" data-testid="oa-controls-provider">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-[640]">AI provider configuration</h3>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                Changing a provider or model applies from the next matching run, never mid-run, and
                never rewrites a historical score.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => test.mutate()}
              disabled={test.isPending} data-testid="oa-provider-test">
              {test.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Test connection
            </Button>
          </div>

          {testResult && (
            <p role="status" aria-live="polite"
              className={`mt-2 text-[11px] font-[600] ${
                testResult.ok ? "text-teal-700" : "text-destructive"}`}
              data-testid="oa-provider-test-result">
              {testResult.text}
            </p>
          )}

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {Object.entries(provider.values || {}).map(([field, value]) => (
              <label key={field} className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
                {field.replace(/_/g, " ")}
                <Input defaultValue={value || ""} placeholder={value || "not set"}
                  onChange={(e) => setValues((s) => ({ ...s, [field]: e.target.value }))}
                  className="mt-1 h-9 text-[13px]" data-testid={`oa-provider-${field}`} />
                <span className="mt-0.5 block text-[10px] font-[400] normal-case tracking-normal">
                  source: {provider.sources?.[field]}
                </span>
              </label>
            ))}
            {Object.entries(provider.secrets || {}).map(([field, info]) => (
              <label key={field} className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
                {field.replace(/_/g, " ")}
                <Input type="password" autoComplete="new-password"
                  placeholder={info.configured ? `configured ${info.mask}` : "not configured"}
                  onChange={(e) => setSecrets((s) => ({ ...s, [field]: e.target.value }))}
                  className="mt-1 h-9 text-[13px]" data-testid={`oa-provider-secret-${field}`} />
                <span className="mt-0.5 block text-[10px] font-[400] normal-case tracking-normal">
                  Write-only. A saved secret can never be read back.
                </span>
              </label>
            ))}
          </div>
          {!provider.crypto_available && (
            <p className="mt-2 text-[11px] text-destructive" data-testid="oa-provider-no-crypto">
              The secret store is unavailable, so a new secret cannot be saved safely right now.
            </p>
          )}
          <Button size="sm" className="mt-3" onClick={() => setProviderOpen(true)}
            data-testid="oa-provider-save">Save provider configuration</Button>
        </Card>
      ) : null}

      <Card className="p-4" data-testid="oa-controls-readonly-config">
        <h3 className="flex items-center gap-1.5 text-sm font-[640]">
          <ShieldCheck className="h-4 w-4 text-teal-700" /> Matching configuration (read-only)
        </h3>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
          {Object.entries(data.read_only || {}).filter(([k]) => k !== "note").map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-muted-foreground">{k.replace(/_/g, " ")}</dt>
              <dd className="text-right font-[600] tabular-nums"
                data-testid={`oa-readonly-${k}`}>{String(v)}</dd>
            </React.Fragment>
          ))}
        </dl>
        <p className="mt-2 text-[11px] italic text-muted-foreground">{data.read_only?.note}</p>
      </Card>

      <OaReasonDialog
        open={!!pending} onOpenChange={(o) => !o && setPending(null)}
        testId="control"
        title={pending?.clear ? "Clear this runtime override?" : "Change this operational control?"}
        description={pending?.clear
          ? "The deployed value will apply again immediately."
          : "This takes effect immediately for every member."}
        target={pending?.key}
        effect={pending?.clear ? "Restores the deployed value exactly."
          : `Sets ${pending?.key} ${pending?.value ? "ON" : "OFF"}. ${
            pending?.value ? "Turning a control on can process a historic backlog." : ""}`}
        reversible allowInternalNote={false}
        confirmLabel={pending?.clear ? "Clear override" : "Apply"}
        invalidate={[["oa-admin-controls"], ["oa-admin-health"]]}
        mutationFn={({ reason }) => api.put(`/admin/otter-assist/controls/${pending.key}`,
          { value: pending.clear ? null : pending.value, reason })}
        onDone={() => setPending(null)} />

      <OaReasonDialog
        open={providerOpen} onOpenChange={setProviderOpen} testId="provider"
        title="Save provider configuration?"
        description="Secrets are encrypted at rest and can never be read back."
        target="AI provider configuration"
        effect="Applies from the next matching run. Historical scores are never relabelled."
        reversible allowInternalNote={false} confirmLabel="Save"
        invalidate={[["oa-admin-controls"], ["oa-admin-health"]]}
        mutationFn={({ reason }) => api.put("/admin/otter-assist/provider",
          { values, secrets, reason })}
        onDone={() => { setSecrets({}); qc.invalidateQueries({ queryKey: ["oa-admin-controls"] }); }} />
    </div>
  );
}

export default OaControls;
